import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import Header from "@/components/Header";

export default function Signup() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Validation states
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordConfirmError, setPasswordConfirmError] = useState("");
  const [authError, setAuthError] = useState("");

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = (pwd) => {
    // Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 number, and 1 special character
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    return regex.test(pwd);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);

    if (pwd && !validatePassword(pwd)) {
      setPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters."
      );
    } else {
      setPasswordError("");
    }

    // Check password match if confirm password is filled
    if (passwordConfirm && pwd !== passwordConfirm) {
      setPasswordConfirmError("Passwords do not match");
    } else if (passwordConfirm) {
      setPasswordConfirmError("");
    }
  };

  const handlePasswordConfirmChange = (e) => {
    const pwd = e.target.value;
    setPasswordConfirm(pwd);

    if (pwd && pwd !== password) {
      setPasswordConfirmError("Passwords do not match");
    } else {
      setPasswordConfirmError("");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password does not meet requirements");
      return;
    }

    if (password !== passwordConfirm) {
      setPasswordConfirmError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setAuthError("");

    const { error } = await signUp(email, password, {
      firstName,
      lastName,
    });

    if (error) {
      setAuthError(error.message);
      setIsLoading(false);
    } else {
      // Show success message
      setAuthError("");
      setIsLoading(false);
      setShowSuccess(true);
    }
  };

  const isFormValid =
    email &&
    validateEmail(email) &&
    !emailError &&
    firstName &&
    lastName &&
    password &&
    passwordConfirm &&
    !passwordError &&
    !passwordConfirmError &&
    agreed;

  return (
    <div className="relative">
      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 max-w-sm w-full shadow-xl">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                Registration Successful!
              </h3>
              <p className="text-sm text-muted-foreground text-center">
                Please check your email to confirm your account.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full mt-4"
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <Header showBack={true} />

      <div className="p-4">
        <div className="w-full max-w-md mx-auto flex flex-col gap-6 md:gap-10 mt-6 md:mt-12">
          <h2 className="text-2xl font-bold text-foreground">
            Create Your Account
          </h2>

          <form onSubmit={handleRegister} className="space-y-5">
            {/* Email */}
            <div className="space-y-1">
              <Label
                htmlFor="email"
                className="text-xs text-muted-foreground font-normal"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder=""
                value={email}
                onChange={handleEmailChange}
                required
                className="h-12 px-2 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
              />
              {emailError && (
                <p className="text-[10px] text-red-500">{emailError}</p>
              )}
            </div>

            {/* First Name */}
            <div className="space-y-1">
              <Label
                htmlFor="firstName"
                className="text-xs text-muted-foreground font-normal"
              >
                First Name
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder=""
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="h-12 px-2 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
              />
            </div>

            {/* Last Name */}
            <div className="space-y-1">
              <Label
                htmlFor="lastName"
                className="text-xs text-muted-foreground font-normal"
              >
                Last Name
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder=""
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                className="h-12 px-2 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
              />
            </div>

            {/* Password */}
            <div className="space-y-1">
              <Label
                htmlFor="password"
                className="text-xs text-muted-foreground font-normal"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder=""
                value={password}
                onChange={handlePasswordChange}
                required
                className="h-12 px-2 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
              />
              {passwordError && (
                <p className="text-[10px] text-red-500">{passwordError}</p>
              )}
              {!passwordError && password && (
                <p className="text-[10px] text-muted-foreground">
                  Password must be at least 8 characters and include uppercase,
                  lowercase, numbers, and special characters.
                </p>
              )}
            </div>

            {/* Password Confirm */}
            <div className="space-y-1">
              <Label
                htmlFor="passwordConfirm"
                className="text-xs text-muted-foreground font-normal"
              >
                Confirm Password
              </Label>
              <Input
                id="passwordConfirm"
                type="password"
                placeholder=""
                value={passwordConfirm}
                onChange={handlePasswordConfirmChange}
                required
                className="h-12 px-2 bg-transparent border-0 border-b rounded-none placeholder:text-muted-foreground text-sm focus-visible:ring-0"
              />
              {passwordConfirmError && (
                <p className="text-[10px] text-red-500">
                  {passwordConfirmError}
                </p>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="terms"
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-0.5"
              />
              <label
                htmlFor="terms"
                className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
              >
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            {authError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <p className="text-xs text-red-600 dark:text-red-400">
                  {authError}
                </p>
              </div>
            )}

            {/* Register Button */}
            <Button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="w-full h-12 mt-4"
            >
              {isLoading ? "Creating Account..." : "Sign Up"}
            </Button>
          </form>

          {/* Login Link */}
          <div className="text-center">
            <span className="text-sm text-muted-foreground">
              Already have an account?{" "}
            </span>
            <Link
              to="/login"
              className="text-sm font-bold text-primary hover:underline"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
